import type {
    LogChannel,
    LogEntry,
    LogSearchResponse,
    LogUser,
} from "../../../core/types.js";
import { SharedLogSearchRequestQueue } from "../../../utils/logSearchRequests.js";

type LogSearchQueue = {
    fetchJson<TPayload extends Record<string, unknown>>(url: string, options?: RequestInit): Promise<TPayload>;
};

type BanContextResponse = LogSearchResponse & {
    result: LogEntry[];
};

export type BanCommandDetection = {
    confidence: "high" | "medium";
    pattern: string;
    log: LogEntry;
};

export type BanContextResult = {
    messages: LogEntry[];
    members: LogUser[];
    channels: LogChannel[];
    command: BanCommandDetection | null;
};

type BanAction = "ban" | "unban";

type BanCommandPattern = {
    label: string;
    pattern: RegExp;
};

type BanContextActionConfig = {
    userCommandRegex: string;
    botGeneratedLogRegex: string;
    commandPatterns: BanCommandPattern[];
};

const BanContextWindowMs = 10_000;
const MassUnbanFallbackWindowMs = 30 * 60 * 1000;
const BanCommandFallbackLimit = 5;
const BanContextLimit = 25;
const BanContextLogTypes = [ "message_create", "message_edit" ];
const MassUnbanCommandRegex = "(^|[^a-z0-9_])[./!&?]?(clearbl|clearblog|unbanall)([^a-z0-9_]|$)";
const MassUnbanCommandPatterns: BanCommandPattern[] = [
    { label: ".clearbl", pattern: /(^|\s)[./!&?]?clearbl\b/i },
    { label: ".clearblog", pattern: /(^|\s)[./!&?]?clearblog\b/i },
    { label: ".unbanall", pattern: /(^|\s)[./!&?]?unbanall\b/i },
];
const BanContextActionConfigs: Record<BanAction, BanContextActionConfig> = {
    ban: {
        userCommandRegex     : "(^|[^a-z0-9_])[./!&?+]?(ban|blacklist|blog|kick|bl)([^a-z0-9_]|$)",
        botGeneratedLogRegex : "(blacklist|ban|kick|blog)",
        commandPatterns      : [
            { label: ".ban", pattern: /(^|\s)[./!&?+]?ban\b/i },
            { label: ".blacklist", pattern: /(^|\s)[./!&?+]?blacklist\b/i },
            { label: ".bl", pattern: /(^|\s)[./!&?+]?bl\b/i },
            { label: ".blog", pattern: /(^|\s)[./!&?+]?blog\b/i },
            { label: ".kick", pattern: /(^|\s)[./!&?+]?kick\b/i },
        ],
    },
    unban: {
        userCommandRegex     : "(^|[^a-z0-9_])[./!&?]?(unban|unbl|deblog|unblog)([^a-z0-9_]|$)",
        botGeneratedLogRegex : "(deban|déban|unban)",
        commandPatterns      : [
            { label: ".unban", pattern: /(^|\s)[./!&?]?unban\b/i },
            { label: ".unbl", pattern: /(^|\s)[./!&?]?unbl\b/i },
            { label: ".deblog", pattern: /(^|\s)[./!&?]?deblog\b/i },
            { label: ".unblog", pattern: /(^|\s)[./!&?]?unblog\b/i },
        ],
    },
};

function createJsonPostOptions(body: Record<string, unknown>): RequestInit {
    return {
        headers: {
            "content-type": "application/json",
        },
        body    : JSON.stringify(body),
        method  : "POST",
    };
}

function getSafeWindow(createdAt: number): { after: number; before: number } {
    const now = Date.now();
    const before = Math.min(createdAt + BanContextWindowMs, now);

    return {
        after: Math.min(Math.max(0, createdAt - BanContextWindowMs), Math.max(0, before - 1)),
        before,
    };
}

function getMassUnbanFallbackWindow(createdAt: number): { after: number; before: number } {
    const now = Date.now();
    const before = Math.min(createdAt + BanContextWindowMs, now);

    return {
        after: Math.max(0, createdAt - MassUnbanFallbackWindowMs),
        before,
    };
}

function collectStrings(value: unknown, result: string[]): void {
    if (typeof value === "string") {
        result.push(value);
        return;
    }

    if (Array.isArray(value)) {
        for (const item of value)
            collectStrings(item, result);
        return;
    }

    if (!value || typeof value !== "object")
        return;

    for (const item of Object.values(value))
        collectStrings(item, result);
}

function getSearchableMessageText(log: LogEntry): string {
    const values: string[] = [];

    collectStrings(log.extra, values);

    return values.join("\n");
}

function mentionsTarget(content: string, targetId: string | null | undefined): boolean {
    return targetId ? content.includes(targetId) : false;
}

function getBanAction(log: LogEntry): BanAction {
    return log.type === "member_ban_remove" ? "unban" : "ban";
}

function findBanCommand(
    logs: readonly LogEntry[],
    targetId: string | null | undefined,
    commandPatterns: readonly BanCommandPattern[],
): BanCommandDetection | null {
    for (const log of logs) {
        const content = getSearchableMessageText(log);

        if (!mentionsTarget(content, targetId))
            continue;

        for (const commandPattern of commandPatterns) {
            if (commandPattern.pattern.test(content)) {
                return {
                    confidence : "high",
                    pattern    : commandPattern.label,
                    log,
                };
            }
        }
    }

    return null;
}

function findMassUnbanCommand(logs: readonly LogEntry[]): BanCommandDetection | null {
    for (const log of logs) {
        const content = getSearchableMessageText(log);

        for (const commandPattern of MassUnbanCommandPatterns) {
            if (commandPattern.pattern.test(content)) {
                return {
                    confidence : "medium",
                    pattern    : commandPattern.label,
                    log,
                };
            }
        }
    }

    return null;
}

function quoteExpressionString(value: string): string {
    return JSON.stringify(value);
}

function buildTypeSet(values: readonly string[]): string {
    return values.map(quoteExpressionString).join(", ");
}

function buildBanContextExpression(log: LogEntry, currentBotId: string): string {
    const actionConfig = BanContextActionConfigs[getBanAction(log)];
    const targetClause = log.target
        ? ` and contains(extra, ${quoteExpressionString(log.target)})`
        : "";
    const userCommandClause = `match(lower(extra.content), ${quoteExpressionString(actionConfig.userCommandRegex)})`;
    const botLogClause = [
        `executor == ${quoteExpressionString(currentBotId)}`,
        `match(lower(extra), ${quoteExpressionString(actionConfig.botGeneratedLogRegex)})`,
    ].join(" and ");

    return [
        `type in { ${buildTypeSet(BanContextLogTypes)} }`,
        targetClause,
        ` and ((${userCommandClause}) or (${botLogClause}))`,
    ].join("");
}

function buildMassUnbanContextExpression(): string {
    return [
        `type in { ${buildTypeSet(BanContextLogTypes)} }`,
        ` and match(lower(extra.content), ${quoteExpressionString(MassUnbanCommandRegex)})`,
    ].join("");
}

function buildBanCommandFallbackExpression(log: LogEntry): string | null {
    if (!log.target)
        return null;

    const actionConfig = BanContextActionConfigs.ban;

    return [
        `type == "message_create"`,
        ` and contains(extra.content, ${quoteExpressionString(log.target)})`,
        ` and match(lower(extra.content), ${quoteExpressionString(actionConfig.userCommandRegex)})`,
    ].join("");
}

async function fetchBanContextLogs({
    after,
    before,
    expression,
    limit = BanContextLimit,
    orderBy = "ASC",
    queue,
    signal,
}: {
    after?: number;
    before?: number;
    expression: string;
    limit?: number;
    orderBy?: "ASC" | "DESC";
    queue: LogSearchQueue;
    signal?: AbortSignal;
}): Promise<BanContextResponse> {
    const params = new URLSearchParams({
        limit   : String(limit),
        orderBy,
    });

    if (after !== undefined)
        params.set("after", String(after));

    if (before !== undefined)
        params.set("before", String(before));

    const body = await queue.fetchJson<BanContextResponse>(`/api/logs?${params.toString()}`, {
        ...createJsonPostOptions({
            mode  : "expression",
            query : expression,
        }),
        signal,
    });

    if (body.error)
        throw new Error(body.error);

    return body;
}

function createMemberMap(members: readonly LogUser[]): Map<string, LogUser> {
    return new Map(members
        .filter((member) => member?.id)
        .map((member) => [ member.id, member ]));
}

function isBot(member: LogUser | undefined): boolean {
    return Boolean(member?.user && "bot" in member.user && member.user.bot === true);
}

function pickDisplayMessages({
    auditExecutor,
    command,
    logs,
    members,
}: {
    auditExecutor: string | null | undefined;
    command: BanCommandDetection | null;
    logs: readonly LogEntry[];
    members: readonly LogUser[];
}): LogEntry[] {
    const memberMap = createMemberMap(members);
    const commandExecutor = command?.log.executor ?? null;
    const actorIds = new Set<string>();

    if (commandExecutor)
        actorIds.add(commandExecutor);

    if (auditExecutor)
        actorIds.add(auditExecutor);

    const filtered = logs.filter((log) => {
        if (log.executor && actorIds.has(log.executor))
            return true;

        return isBot(log.executor ? memberMap.get(log.executor) : undefined);
    });

    return (filtered.length ? filtered : logs)
        .slice(0, BanContextLimit);
}

export async function loadBanContext(
    log: LogEntry,
    currentBotId: string,
    {
        queue = SharedLogSearchRequestQueue,
        signal,
    }: {
        queue?: LogSearchQueue;
        signal?: AbortSignal;
    } = {},
): Promise<BanContextResult> {
    const action = getBanAction(log);
    const window = getSafeWindow(log.createdAt);
    let body = await fetchBanContextLogs({
        after      : window.after,
        before     : window.before,
        expression : buildBanContextExpression(log, currentBotId),
        queue,
        signal,
    });
    let logs = (body.result ?? [])
        .sort((left, right) => left.createdAt - right.createdAt);
    let command = findBanCommand(logs, log.target, BanContextActionConfigs[action].commandPatterns);
    let messages = pickDisplayMessages({
        auditExecutor : log.executor,
        command,
        logs,
        members       : body.members ?? [],
    });

    if (action === "ban" && !command) {
        const fallbackExpression = buildBanCommandFallbackExpression(log);

        if (fallbackExpression) {
            const fallbackBody = await fetchBanContextLogs({
                expression : fallbackExpression,
                limit      : BanCommandFallbackLimit,
                orderBy    : "DESC",
                queue,
                signal,
            });
            const fallbackLogsByRecency = fallbackBody.result ?? [];
            const fallbackCommand = findBanCommand(fallbackLogsByRecency, log.target, BanContextActionConfigs.ban.commandPatterns);

            if (fallbackCommand) {
                body = fallbackBody;
                command = fallbackCommand;
                logs = fallbackLogsByRecency.sort((left, right) => left.createdAt - right.createdAt);
                messages = pickDisplayMessages({
                    auditExecutor : log.executor,
                    command,
                    logs,
                    members       : body.members ?? [],
                });
            }
        }
    }

    if (action === "unban" && !messages.length) {
        const fallbackWindow = getMassUnbanFallbackWindow(log.createdAt);
        const fallbackBody = await fetchBanContextLogs({
            after      : fallbackWindow.after,
            before     : fallbackWindow.before,
            expression : buildMassUnbanContextExpression(),
            orderBy    : "DESC",
            queue,
            signal,
        });
        const fallbackLogsByRecency = fallbackBody.result ?? [];
        const fallbackCommand = findMassUnbanCommand(fallbackLogsByRecency);

        if (fallbackCommand) {
            body = fallbackBody;
            command = fallbackCommand;
            logs = fallbackLogsByRecency.sort((left, right) => left.createdAt - right.createdAt);
            messages = pickDisplayMessages({
                auditExecutor : log.executor,
                command,
                logs,
                members       : body.members ?? [],
            });
        }
    }

    return {
        messages,
        members     : body.members ?? [],
        channels    : body.channels ?? [],
        command,
    };
}
